import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { Navigate } from 'react-router-dom';
import { z } from 'zod';

import Logo from '@/components/common/Logo';
import { RhfStateCitySelect } from '@/components/common/RhfStateCitySelect';
import { Button, Card, DatePicker, Input, SelectField, useToast } from '@/components/ui';
import useAuth from '@/hooks/useAuth';
import { useDesignations } from '@/hooks/useCompanySettings';
import { useCompleteOnboarding } from '@/hooks/useUsers';
import { optionsWithStored } from '@/lib/designations';
import { errorMessage } from '@/utils/apiError';
import { departmentForJobRole, homeRouteFor } from '@/utils/constants';
import { todayIso } from '@/utils/format';

/**
 * ONE onboarding form for every role, and every field is required (Fix 4).
 *
 * Billing used to get a three-field variant with no address, pincode, city,
 * state or employee code. That left back-office employees permanently missing
 * what every other employee's record had, with no screen that would ever ask
 * them for it again.
 *
 * `designationDept` is NOT in this schema. The department is decided by the job
 * role the employee requested on the Request Access form and is derived
 * server-side; the form shows it, read-only, so they can see what they are
 * being onboarded into without being asked to answer it a second time.
 *
 * `employeeCode` is the required, unique Employee Code: it is printed into every
 * quotation/renewal reference number, so an employee without one cannot send a
 * referenced email at all, and two sharing one make those references
 * permanently ambiguous. Uniqueness is enforced by the server - the only place
 * it CAN be, since a form cannot know what other employees hold.
 */
const onboardingSchema = z.object({
  dateOfBirth: z.string().min(1, 'Date of birth is required'),
  dateOfJoining: z.string().min(1, 'Date of joining is required'),
  designationLevel: z.string().trim().min(1, 'Designation level is required').max(50),
  address: z.string().trim().min(1, 'Address is required').max(255),
  pincode: z
    .string()
    .trim()
    .regex(/^\d{4,10}$/, 'Enter a valid pincode'),
  city: z.string().trim().min(1, 'City is required').max(100),
  state: z.string().trim().min(1, 'State is required').max(100),
  employeeCode: z
    .string()
    .trim()
    .regex(/^[A-Za-z0-9]{1,10}$/, 'Use 1-10 letters/digits'),
});

type OnboardingValues = z.infer<typeof onboardingSchema>;

const OnboardingPage = () => {
  const { user, logout } = useAuth();
  const { toast } = useToast();
  const completeOnboarding = useCompleteOnboarding();
  // Designation LEVEL only. The department below is not a dropdown any more, so
  // this list is the only configured vocabulary the form still offers.
  const { data: designations, isLoading: designationsLoading } = useDesignations();

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<OnboardingValues>({
    resolver: zodResolver(onboardingSchema),
    // State/City are written through setValue by RhfStateCitySelect, which needs
    // them to exist as controlled values from the first render.
    defaultValues: { state: '', city: '' },
  });

  const onSubmit = async (values: OnboardingValues) => {
    try {
      await completeOnboarding.mutateAsync(values);
      toast.success('Welcome aboard! Your details have been saved.');
    } catch (error) {
      // Surfaces the server's own reason verbatim - notably the 409 naming the
      // employee code that is already taken, which is the one failure the user
      // can actually act on.
      toast.error(errorMessage(error, 'Could not save your details. Please try again.'));
    }
  };

  // Already done (e.g. back button after completion) - nothing to collect.
  // Route by the user's actual workspace, not a hardcoded fallback, so a
  // Billing/etc. user lands in their own workspace right after onboarding.
  if (user?.onboardingCompletedAt) {
    return <Navigate to={homeRouteFor(user)} replace />;
  }

  // Mirrors what the server will store. Shown rather than asked: the role was
  // already chosen at Request Access, and an employee who could pick a
  // different department here would create a record that contradicts itself.
  const department = departmentForJobRole(user?.jobRole);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-cream/40 p-4">
      <div className="mb-6">
        <Logo />
      </div>
      <div className="w-full max-w-2xl">
        <Card padding="lg">
          <h1 className="text-2xl font-bold text-ink">Complete your profile</h1>
          <p className="mt-1.5 text-sm text-ink/60">
            Welcome, {user?.name}. Before you continue, fill in your employee details. Every field
            is required. To change any of them later, ask your admin.
          </p>

          <form
            onSubmit={(e) => {
              handleSubmit(onSubmit)(e).catch(() => {});
            }}
            noValidate
            className="mt-6 flex flex-col gap-4"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <Controller
                control={control}
                name="dateOfBirth"
                render={({ field }) => (
                  <DatePicker
                    id="dateOfBirth"
                    label="Date of birth"
                    value={field.value}
                    onChange={field.onChange}
                    max={todayIso()}
                    error={errors.dateOfBirth?.message}
                  />
                )}
              />
              <Controller
                control={control}
                name="dateOfJoining"
                render={({ field }) => (
                  <DatePicker
                    id="dateOfJoining"
                    label="Date of joining"
                    value={field.value}
                    onChange={field.onChange}
                    max={todayIso()}
                    error={errors.dateOfJoining?.message}
                  />
                )}
              />
              <Controller
                name="designationLevel"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <SelectField
                    id="designationLevel"
                    label="Designation level"
                    disabled={designationsLoading}
                    error={errors.designationLevel?.message}
                    placeholder="Select a level"
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value)}
                  >
                    <option value="" disabled>
                      Select a level
                    </option>
                    {optionsWithStored(designations?.prefixes ?? [], user?.designationLevel).map(
                      (option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ),
                    )}
                  </SelectField>
                )}
              />
              {/* Read-only, and not part of the form at all - there is no field
                  behind it and nothing about it is submitted. */}
              <Input
                id="designationDept"
                label="Department"
                type="text"
                value={department ?? '-'}
                helperText="Set by the role you requested. Ask your admin to change it."
                disabled
                readOnly
              />
              <Input
                id="pincode"
                label="Pincode"
                type="text"
                inputMode="numeric"
                placeholder="6-digit pincode"
                error={errors.pincode?.message}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...register('pincode')}
              />
              {/* Dropdowns, not free text (Fix 3). Free-typed geography produced
                  "Bangalore"/"Bengaluru"/"bangalore" as three different cities,
                  which is unfixable once it is in the data. */}
              <RhfStateCitySelect
                control={control}
                setValue={setValue}
                stateName="state"
                cityName="city"
                labelClassName="text-sm font-medium text-foreground"
                stateError={errors.state?.message}
                cityError={errors.city?.message}
              />
              <Input
                id="address"
                label="Posting location address"
                type="text"
                wrapperClassName="sm:col-span-2"
                error={errors.address?.message}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...register('address')}
              />
              <Input
                id="employeeCode"
                label="Employee code"
                type="text"
                maxLength={10}
                placeholder="e.g. SRK"
                error={errors.employeeCode?.message}
                wrapperClassName="sm:col-span-2"
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...register('employeeCode')}
              />
              <p className="-mt-2 text-xs text-ink/50 sm:col-span-2">
                Your unique code across the company, used as the prefix in email reference numbers
                (e.g. AK/QT/2026-27/
                <span className="font-semibold">SRK</span>
                001). If the one you enter is already taken, you&apos;ll be told and can pick
                another.
              </p>
            </div>

            <Button type="submit" disabled={completeOnboarding.isPending} className="mt-2 w-full">
              {completeOnboarding.isPending ? 'Saving…' : 'Save and continue'}
            </Button>
          </form>

          <button
            type="button"
            onClick={() => {
              void logout();
            }}
            className="mt-6 w-full text-center text-xs text-ink/50 hover:text-ink/70 hover:underline"
          >
            Not you? Sign out
          </button>
        </Card>
      </div>
    </div>
  );
};

export default OnboardingPage;
